// 261005 E3-P2a — hai hộp thoại sửa tay chặng ④: sửa tóm tắt/móc · tách/dời điểm cắt (xem thử → Áp). Logic quyết định ở `splitStageView.ts` (thuần, có test).
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Input, Modal, Radio, Select, Space, Spin, Tooltip, Typography, message } from 'antd';
import { adaptationApi } from '../../../services/api/adaptation';
import type { EpisodeAppealView, EpisodeSplitOp, EpisodeSplitResponse, OutlineEpisode, OverviewMotives, SplitCandidatesResponse } from '../../../types/adaptation';
import { chapterMoveChoices, chapterSplitChoices, describeSplitRejected, formatSourceLength, nearbyCuts, SplitMode } from './splitStageView';
import { friendlyApiError } from './jobErrorText';
import {
  APPEAL_CHANGE_NOTE, APPEAL_NOTE_LINK_DROP, APPEAL_REVIEWED_NOTE, APPEAL_KINDS, APPEAL_KIND_HINT, APPEAL_KIND_LABEL, APPEAL_PROMISE_NOTE, appealFormBlocker, appealFormRows, buildEpisodeFormOps, initialAppealDraft,
  droppedNoteLinkClaimIds, lostLabelWarnings, motiveNoteOptions, textDirtyParts, willChangeAppeal, type AppealDraft, type AppealFormKind,
} from './episodeAppealView';

const { Text } = Typography;
const { TextArea } = Input;

const errText = (e: any): { code?: string; text: string } => ({ code: e?.response?.data?.code, text: friendlyApiError(e, 'Không áp được') });

interface BaseProps {
  storyId: string;
  /** `record.modifiedAt` đang hiện trên màn hình — mốc CAS. */
  modifiedAt: string | undefined;
  onClose: () => void;
  /** Sau khi ghi xong (hoặc cần tải lại do mốc lệch). */
  onChanged: () => Promise<unknown> | void;
}

interface TextModalProps extends BaseProps {
  episode: OutlineEpisode | null;
  /** 261015 P4c-2b — ghi chú động cơ của tổng quan HIỆN TẠI (lựa chọn "thực hiện ghi chú"); vắng ⇒ không có ô chọn. */
  motives?: OverviewMotives | null;
  /** `OutlineRecord.appeal` — chỉ để biết tập đang "chưa khai" (NO_BASIS). */
  appeal?: EpisodeAppealView | null;
}

const clipText = (s: string, n: number): string => (s.length > n ? `${s.slice(0, n)}…` : s);

export const EpisodeTextModal: React.FC<TextModalProps> = ({ storyId, modifiedAt, onClose, onChanged, episode, motives, appeal }) => {
  const [v, setV] = useState({ synopsis: '', hook: '', suspense: '' });
  const [draft, setDraft] = useState<AppealDraft>({});
  const [reviewed, setReviewed] = useState(false);
  const [saving, setSaving] = useState(false);
  const rows = useMemo(() => (episode ? appealFormRows(episode) : []), [episode]);
  const noteOptions = useMemo(() => motiveNoteOptions(motives), [motives]);
  useEffect(() => {
    if (episode) { setV({ synopsis: episode.synopsis ?? '', hook: episode.hook ?? '', suspense: episode.suspense ?? '' }); setDraft(initialAppealDraft(appealFormRows(episode))); setReviewed(false); }
  }, [episode]);
  if (!episode) return null;
  const knownNoteIds = new Set(noteOptions.map((n) => n.id));
  const blocker = appealFormBlocker(episode);
  const dirty = textDirtyParts(episode, v);
  const warnings = lostLabelWarnings(episode, v, draft);
  /** Tập chưa khai (BE báo NO_BASIS) mà người chưa gắn nhãn nào ⇒ cho xác nhận "đã xem — không có nhịp" (đặt khoá, Q8-3). */
  const canMarkReviewed = !blocker && appeal?.reasons?.[String(episode.ep)] === 'NO_BASIS' && !rows.some((r) => (draft[r.claimId]?.kind ?? r.kind) !== '');
  const opsNow = buildEpisodeFormOps({ episode, text: v, draft, knownNoteIds, markReviewed: reviewed && canMarkReviewed });
  const changesAppeal = willChangeAppeal(opsNow);
  const droppedLinks = droppedNoteLinkClaimIds(episode, opsNow, knownNoteIds);
  const setRow = (claimId: string, patch: Partial<{ kind: AppealFormKind; noteId: string }>) => setDraft((d) => ({ ...d, [claimId]: { kind: d[claimId]?.kind ?? '', noteId: d[claimId]?.noteId ?? '', ...patch } }));
  const save = async () => {
    setSaving(true);
    try {
      // Thứ tự lệnh do `buildEpisodeFormOps` giữ: setEpisodeAppeal (id ý theo bản đang xem) TRƯỚC editEpisodeText (đánh lại id).
      const r = await adaptationApi.splitPatch(storyId, opsNow, modifiedAt);
      if (!r.written) { message.warning(describeSplitRejected(r.rejected) || 'Không có gì để ghi', 6); return; }
      if (r.rejected.length) message.warning(`Đã lưu một phần tập ${episode.ep} — còn lệnh không áp được: ${describeSplitRejected(r.rejected)}`, 8);
      else message.success(`Đã sửa tập ${episode.ep}`);
      onClose();
      await onChanged();
    } catch (e) {
      const { code, text } = errText(e);
      if (code === 'OUTLINE_CHANGED') { message.warning('Danh sách tập vừa đổi ở nơi khác — đã tải lại, thử lại', 6); onClose(); await onChanged(); } else message.error(text, 6);
    } finally { setSaving(false); }
  };
  return (
    <Modal open title={`Sửa tập ${episode.ep}`} okText="Lưu" cancelText="Huỷ" onOk={save} onCancel={onClose} confirmLoading={saving} destroyOnClose>
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Text strong>Tóm tắt</Text>
        <TextArea rows={4} maxLength={2000} showCount value={v.synopsis} onChange={(e) => setV({ ...v, synopsis: e.target.value })} />
        <Text strong>Móc cuối tập</Text>
        <TextArea rows={2} maxLength={2000} showCount value={v.hook} onChange={(e) => setV({ ...v, hook: e.target.value })} />
        <Text strong>Còn treo (câu hỏi chưa trả lời)</Text>
        <TextArea rows={2} maxLength={2000} showCount value={v.suspense} onChange={(e) => setV({ ...v, suspense: e.target.value })} />
        <Tooltip title={`${APPEAL_PROMISE_NOTE} ${APPEAL_CHANGE_NOTE}`}>
          <Text strong style={{ borderBottom: '1px dotted currentColor', width: 'fit-content' }}>Nhịp hấp dẫn dự kiến</Text>
        </Tooltip>
        {blocker ? <Text type="secondary" style={{ fontSize: 13 }}>{blocker}</Text> : (
          <Space direction="vertical" size={6} style={{ width: '100%' }} data-testid="appeal-form">
            {rows.map((r) => {
              const d = draft[r.claimId] ?? { kind: r.kind, noteId: r.noteId };
              const textDirty = dirty.has(r.part);
              const noteGone = !!d.noteId && !knownNoteIds.has(d.noteId);
              return (
                <div key={r.claimId}>
                  <Text type="secondary" style={{ fontSize: 13 }}>{r.partLabel}: {clipText(r.text, 120)}</Text>
                  <Space wrap size={6} style={{ display: 'flex' }}>
                    <Tooltip title={textDirty ? 'Đã sửa chữ phần này — nhãn của phần sẽ mất, không đặt được cùng lúc' : d.kind ? APPEAL_KIND_HINT[d.kind] : undefined}>
                      <span>
                        <Select
                          size="small" style={{ width: 150 }} value={d.kind} disabled={textDirty} aria-label={`Nhịp hấp dẫn của ý ${r.claimId}`}
                          onChange={(k: AppealFormKind) => setRow(r.claimId, k === '' ? { kind: '', noteId: '' } : { kind: k })}
                          options={[{ value: '', label: 'Không có nhịp' }, ...APPEAL_KINDS.map((k) => ({ value: k, label: APPEAL_KIND_LABEL[k] }))]}
                        />
                      </span>
                    </Tooltip>
                    {d.kind !== '' && noteOptions.length > 0 && (
                      <Select
                        size="small" allowClear style={{ width: 260 }} disabled={textDirty} placeholder="Thực hiện ghi chú động cơ (tuỳ chọn)" aria-label={`Ghi chú động cơ của ý ${r.claimId}`}
                        value={d.noteId || undefined} onChange={(n?: string) => setRow(r.claimId, { noteId: n ?? '' })}
                        options={[
                          ...(noteGone ? [{ value: d.noteId, label: 'Ghi chú cũ (không còn trong tổng quan)', disabled: true }] : []),
                          ...noteOptions.map((n) => ({ value: n.id, label: n.label })),
                        ]}
                      />
                    )}
                  </Space>
                  {droppedLinks.has(r.claimId) && <Text type="warning" style={{ fontSize: 13 }}>{APPEAL_NOTE_LINK_DROP}</Text>}
                </div>
              );
            })}
            {canMarkReviewed && (
              <Tooltip title={APPEAL_REVIEWED_NOTE}>
                <Checkbox checked={reviewed} onChange={(e) => setReviewed(e.target.checked)}>Đã xem — tập này không có nhịp hấp dẫn</Checkbox>
              </Tooltip>
            )}
            {changesAppeal && <Text type="warning" style={{ fontSize: 13 }}>{APPEAL_CHANGE_NOTE}</Text>}
          </Space>
        )}
        {warnings.map((w) => <Alert key={w} type="warning" showIcon message={w} />)}
      </Space>
    </Modal>
  );
};

interface CutProps extends BaseProps {
  kind: 'split' | 'move';
  mode: SplitMode;
  episode: OutlineEpisode | null;
  next: OutlineEpisode | null;
}

/** Tách (`split`) hoặc dời điểm cắt (`move`): chọn điểm → xem thử (độ dài nguồn trước/sau) → Áp. */
export const CutModal: React.FC<CutProps> = ({ storyId, modifiedAt, onClose, onChanged, kind, mode, episode, next }) => {
  const [chapter, setChapter] = useState<number | null>(null);
  const [cands, setCands] = useState<SplitCandidatesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [op, setOp] = useState<EpisodeSplitOp | null>(null);
  const [preview, setPreview] = useState<EpisodeSplitResponse | null>(null);
  const [applying, setApplying] = useState(false);

  const ranges = episode?.sourceRanges ?? [];
  const nextRanges = next?.sourceRanges ?? [];
  /** `range`: chương có thể chọn điểm cắt. Tách ⇒ mọi chương của tập; dời ⇒ chương chứa ranh giới hiện tại (hoặc hai chương nếu ranh giới đúng biên chương). */
  const chapterOptions = useMemo(() => {
    if (!episode) return [] as number[];
    if (kind === 'split') return Array.from(new Set(ranges.map((r) => r.orderIndex)));
    const last = ranges[ranges.length - 1], first = nextRanges[0];
    return Array.from(new Set([last?.orderIndex, first?.orderIndex].filter((x): x is number => typeof x === 'number')));
  }, [episode, kind, ranges, nextRanges]);

  useEffect(() => { setChapter(chapterOptions[0] ?? null); setOp(null); setPreview(null); setCands(null); }, [episode, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (mode !== 'range' || chapter === null || !episode) return;
    let alive = true;
    setLoading(true); setCands(null);
    adaptationApi.splitCandidates(storyId, chapter).then((r) => { if (alive) setCands(r); }).catch((e) => message.error(errText(e).text, 6)).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [mode, chapter, storyId, episode]);

  if (!episode) return null;

  /** Pivot + cửa sổ của chương đang chọn. */
  const geometry = (): { pivot: number; window: { min: number; max: number } } | null => {
    if (!cands || chapter === null) return null;
    const mine = ranges.filter((r) => r.orderIndex === chapter);
    const theirs = nextRanges.filter((r) => r.orderIndex === chapter);
    if (kind === 'split') {
      if (!mine.length) return null;
      const min = mine[0].startOffset, max = mine[mine.length - 1].endOffset;
      return { pivot: Math.floor((min + max) / 2), window: { min, max } };
    }
    const lastR = ranges[ranges.length - 1];
    const min = mine.length ? mine[0].startOffset : 0;
    const max = theirs.length ? theirs[theirs.length - 1].endOffset : cands.length;
    const pivot = lastR.orderIndex === chapter ? lastR.endOffset : 0;
    return { pivot, window: { min, max } };
  };
  const geo = geometry();
  const near = cands && geo ? nearbyCuts(cands.content, cands.candidates, geo.pivot, geo.window, 10) : null;

  const runPreview = async (o: EpisodeSplitOp) => {
    setOp(o); setPreview(null);
    try { setPreview(await adaptationApi.splitPreview(storyId, [o])); } catch (e) { message.error(errText(e).text, 6); }
  };
  const pickRange = (offset: number) => {
    if (chapter === null) return;
    runPreview(kind === 'split' ? { op: 'splitEpisode', ep: episode.ep, at: { orderIndex: chapter, offset } } : { op: 'moveCut', ep: episode.ep, to: { orderIndex: chapter, offset } });
  };
  const apply = async () => {
    if (!op || !preview) return;
    if (preview.baseModifiedAt && modifiedAt && preview.baseModifiedAt !== modifiedAt) {
      message.warning('Danh sách tập vừa đổi ở nơi khác — đã tải lại, xem lại rồi thử lại', 6); onClose(); await onChanged(); return;
    }
    setApplying(true);
    try {
      const r = await adaptationApi.splitPatch(storyId, [op], preview.baseModifiedAt);
      if (!r.written) { message.warning(describeSplitRejected(r.rejected) || 'Không áp được', 6); return; }
      message.success(r.applied.map((a) => a.detail).join('; '));
      onClose();
      await onChanged();
    } catch (e) {
      const { code, text } = errText(e);
      if (code === 'OUTLINE_CHANGED') { message.warning('Danh sách tập vừa đổi ở nơi khác — đã tải lại, thử lại', 6); onClose(); await onChanged(); } else message.error(text, 6);
    } finally { setApplying(false); }
  };

  const rejectedText = preview && preview.rejected.length ? describeSplitRejected(preview.rejected) : '';
  const title = kind === 'split' ? `Tách tập ${episode.ep}` : `Dời điểm cắt giữa tập ${episode.ep} và ${episode.ep + 1}`;

  return (
    <Modal open title={title} width={720} onCancel={onClose} destroyOnClose
      footer={[
        <Button key="x" onClick={onClose}>Huỷ</Button>,
        <Button key="ok" type="primary" loading={applying} disabled={!preview || !preview.applied.length} onClick={apply}>Áp</Button>,
      ]}>
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        {mode === 'chapter' ? (
          kind === 'split' ? (
            <Radio.Group onChange={(e) => runPreview({ op: 'splitEpisode', ep: episode.ep, at: { afterChapter: e.target.value } })}>
              <Space direction="vertical">
                {chapterSplitChoices(episode.sourceChapters).map((c) => <Radio key={c} value={c}>Cắt sau chương {c}</Radio>)}
                {!chapterSplitChoices(episode.sourceChapters).length && <Text type="secondary">Tập chỉ có 1 chương — không tách được theo chương.</Text>}
              </Space>
            </Radio.Group>
          ) : (
            <Radio.Group onChange={(e) => { const c = chapterMoveChoices(episode.sourceChapters, next?.sourceChapters).find((x) => x.direction === e.target.value); if (c) runPreview({ op: 'moveCut', ep: episode.ep, to: { moveChapter: c.chapter, direction: c.direction } }); }}>
              <Space direction="vertical">
                {chapterMoveChoices(episode.sourceChapters, next?.sourceChapters).map((c) => (
                  <Radio key={c.direction} value={c.direction} disabled={!c.enabled}>{c.label}{c.reason ? ` (${c.reason})` : ''}</Radio>
                ))}
              </Space>
            </Radio.Group>
          )
        ) : (
          <>
            {chapterOptions.length > 1 && (
              <Space>
                <Text>Chương:</Text>
                <Select value={chapter ?? undefined} onChange={(v) => { setChapter(v); setOp(null); setPreview(null); }} style={{ width: 160 }}
                  options={chapterOptions.map((c) => ({ value: c, label: `Chương ${c}` }))} />
              </Space>
            )}
            {loading && <Spin />}
            {near && (
              <Radio.Group onChange={(e) => pickRange(e.target.value)} style={{ width: '100%' }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {near.before.map((c) => <Radio key={c.offset} value={c.offset}><Text style={{ fontSize: 13 }}>{c.label}</Text></Radio>)}
                  <Text type="secondary" style={{ fontSize: 13 }}>{kind === 'split' ? '── giữa tập (gợi ý) ──' : '── ranh giới hiện tại ──'}</Text>
                  {near.after.map((c) => <Radio key={c.offset} value={c.offset}><Text style={{ fontSize: 13 }}>{c.label}</Text></Radio>)}
                  {!near.before.length && !near.after.length && <Text type="secondary">Không có điểm cắt hợp lệ trong chương này.</Text>}
                </Space>
              </Radio.Group>
            )}
          </>
        )}
        {preview && preview.applied.length > 0 && preview.outlinePreview && (
          <Alert type="info" showIcon message="Kết quả nếu áp"
            description={(
              <div style={{ fontSize: 13 }}>
                {preview.outlinePreview.before.map((b) => <div key={`b${b.ep}`}>Trước — tập {b.ep}: {formatSourceLength(b.sourceChars, b.sourceChapters)}{b.sourceChapters.length ? ` · chương ${b.sourceChapters.join(', ')}` : ''}</div>)}
                {preview.outlinePreview.after.map((a) => <div key={`a${a.ep}`}>Sau — tập {a.ep}: {formatSourceLength(a.sourceChars, a.sourceChapters)}{a.sourceChapters.length ? ` · chương ${a.sourceChapters.join(', ')}` : ''}</div>)}
                <div>Hai tập bị đụng sẽ được đánh dấu: Cần chấm lại sau khi sửa.</div>
              </div>
            )} />
        )}
        {rejectedText && <Alert type="warning" showIcon message={rejectedText} />}
      </Space>
    </Modal>
  );
};
